import { HitsReportDataRequestDTO } from 'business/models/Models';
import ScoreGroupService from 'business/services/PlateService';
import { TreeService } from 'business/services/TreeService';
import { RMModal, LoadingModal } from 'components/modal';
import React, { useEffect, useMemo, useState } from 'react';
import { CaptureProfileOption } from './ExperimentReportOptions';
import { HitsReportCaptureProfileTable } from './HitsReportCaptureProfileTable';
import { FmlxCheckBox, FmlxModal } from 'fmlx-common-ui';
import WellService from 'business/services/WellService';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import './experimentReports.css';
import { createHitReportPDF, HitsReportOptions } from './HitReportPDF';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import EScoreGroupName from 'enums/EScoreGroupName';
import { getExplorerPlateSelection } from 'utils/explorerPlateSelection';

type ScoreOption = {
  id: number;
  name: string;
  selected: boolean;
};

type ScoreGroupOption = {
  id: number;
  name: string;
  expanded: boolean;
  scores: ScoreOption[];
  selected: boolean;
};

function getCheckboxState(items: { selected: boolean }[]) {
  if (items.length === 0) {
    return { checked: false, indeterminate: false };
  }
  const selectedCount = items.filter((item) => item.selected).length;
  return {
    checked: selectedCount === items.length,
    indeterminate: selectedCount > 0 && selectedCount < items.length,
  };
}

export const HitsReportOptionsDialogue = ({
  nodeIds,
  show,
  setShow,
  showNoHitError,
  setShowNoHitError,
  maxNumOfDrops,
}: any) => {
  const [scoreGroups, setScoreGroups] = useState<ScoreGroupOption[]>([]);
  const [captureProfiles, setCaptureProfiles] = useState<CaptureProfileOption[]>([]);
  const [latestInspectionOnly, setLatestInspectionOnly] = useState<boolean>(false);
  const [uvHitsOnly, setUvHitsOnly] = useState<boolean>(false);
  const [selectedDrop, setSelectedDrop] = useState<number>(1);
  const [isLoading, setIsloading] = useState(false);
  const [generateReportDisabled, setGenerateReportDisabled] = useState(false);
  const isSherlock4Selected = useMemo(
    () =>
      scoreGroups.some(
        (sg) =>
          sg.name?.trim().toLowerCase() === EScoreGroupName.Sherlock4.toLowerCase() &&
          sg.scores.some((score) => score.selected),
      ),
    [scoreGroups],
  );

  const captureProfileColumns = useMemo(
    () => [
      { id: 'name', key: 'name', title: 'Imaging Settings', textAlign: 'left' as const },
      { id: 'selected', key: 'selected', title: 'Selected', textAlign: 'right' as const },
    ],
    [],
  );

  const selectedCaptureProfileCount = useMemo(
    () => captureProfiles.filter((cp) => cp.selected).length,
    [captureProfiles],
  );

  useEffect(() => {
    if (!show) {
      return;
    }
    setLatestInspectionOnly(false);
    setUvHitsOnly(false);
    setSelectedDrop(1);
    retreiveScoreGroups();
    retrieveCaptureProfiles();
  }, [show]);

  const allScores = useMemo(() => scoreGroups.flatMap((sg) => sg.scores), [scoreGroups]);
  const selectAllCheckboxState = useMemo(() => getCheckboxState(allScores), [allScores]);

  useEffect(() => {
    setGenerateReportDisabled(
      scoreGroups.flatMap((sg) => sg.scores).filter((score) => score.selected).length < 1,
    );
  }, [scoreGroups]);

  useEffect(() => {
    if (!isSherlock4Selected) {
      setUvHitsOnly(false);
    }
  }, [isSherlock4Selected]);

  async function retreiveScoreGroups() {
    const [scoreGroups, scores] = await Promise.all([
      new ScoreGroupService().getScoreGroupsService(),
      new WellService().getWellDropScores(),
    ]);
    const scoreGroupOptions = scoreGroups.map((sg) => {
      const groupedScores: ScoreOption[] = scores
        .filter((score) => score.wellDropScoreGroupId === sg.scoreGroupsId)
        .sort((a, b) => a.orderNumber - b.orderNumber)
        .map((score) => ({
          id: score.wellDropScoreId,
          name: score.scoreName,
          selected: true,
        }));

      return {
        id: sg.scoreGroupsId,
        name: sg.name,
        expanded: false,
        scores: groupedScores,
      } as ScoreGroupOption;
    });
    setScoreGroups(scoreGroupOptions);
  }

  async function retrieveCaptureProfiles() {
    const captureProfiles = await new ScoreGroupService().getCaptureProfiles();
    const captureProfileOptions = captureProfiles.captureProfileRows.map(
      (cp) => ({ id: cp.id, name: cp.name, selected: false } as CaptureProfileOption),
    );
    setCaptureProfiles(captureProfileOptions);
  }

  async function hitReportsHandler(saveReport: boolean) {
    const treeService = new TreeService();
    const wellDropScoreIds = scoreGroups
      .flatMap((sg) => sg.scores)
      .filter((score) => score.selected)
      .map((score) => score.id);
    if (!uvHitsOnly) {
      const isHitAvailable = await treeService.IsAnyTreeNodeContainHits(nodeIds);
      if (!isHitAvailable) {
        setShow(false);
        setShowNoHitError?.(true);
        return;
      }
    }
    const scoreGroupIds = scoreGroups
      .filter((sg) => sg.scores.some((scores) => scores.selected))
      .map((sg) => sg.id);
    setShow(false);
    setIsloading(true);
    const plateSelection = getExplorerPlateSelection();
    const hitsReportDataRequestDTO: HitsReportDataRequestDTO =
      new HitsReportDataRequestDTO({
        nodeIds,
        dropNumbers: [selectedDrop],
        wellDropScoreIds,
        captureProfiles: captureProfiles
          .filter((cp) => cp.selected === true)
          .map((cp) => cp.id),
        latestOnly: latestInspectionOnly,
        uvHitsOnly,
        scoreGroupIds: scoreGroupIds,
        ...(plateSelection.plateIds.length > 0 && plateSelection.experimentId != null
          ? {
              plateIds: plateSelection.plateIds,
              plateFilterExperimentId: plateSelection.experimentId,
            }
          : {}),
      });
    const options: HitsReportOptions = {
      latestInspectionOnly: latestInspectionOnly,
      uvHitsOnly: uvHitsOnly,
    };

    const data = await treeService.getHitReportData(hitsReportDataRequestDTO);
    createHitReportPDF(data, saveReport, options);
    setIsloading(false);
  }

  function captureProfilesChangeHandler(checked: boolean, profile: CaptureProfileOption) {
    setCaptureProfiles((prev) =>
      prev.map((cp) => (cp.id === profile.id ? { ...cp, selected: checked } : cp)),
    );
  }

  function scoreGroupsChangeHandler(checked: boolean, groupId: number) {
    setScoreGroups((prevGroups) =>
      prevGroups.map((sg) =>
        sg.id === groupId
          ? {
              ...sg,
              scores: sg.scores.map((score) => ({ ...score, selected: checked })),
            }
          : sg,
      ),
    );
  }

  function allGroupSelectionHandler(checked: boolean) {
    setScoreGroups((prevGroups) =>
      prevGroups.map((sg) => ({
        ...sg,
        scores: sg.scores.map((score) => ({ ...score, selected: checked })),
      })),
    );
  }

  function scoreSelectionHandler(checked: boolean, groupId: number, scoreId: number) {
    setScoreGroups((prevGroups) =>
      prevGroups.map((sg) => {
        if (sg.id !== groupId) {
          return sg;
        }

        return {
          ...sg,
          scores: sg.scores.map((score) =>
            score.id === scoreId ? { ...score, selected: checked } : score,
          ),
        };
      }),
    );
  }

  function toggleGroupCollapse(groupId: number) {
    setScoreGroups((prevGroups) =>
      prevGroups.map((sg) =>
        sg.id === groupId ? { ...sg, expanded: !sg.expanded } : sg,
      ),
    );
  }
  function createOptions() {
    const options = [];
    for (let i = 1; i <= maxNumOfDrops; i++) {
      options.push(
        <option key={'option_' + i} value={i}>
          {i}
        </option>,
      );
    }
    return options;
  }

  return (
    <>
      <RMModal
        id="hitsreportoptionsdialogue-modal-wrapper"
        showModal={show}
        setShowModal={setShow}
        dialogClassName="modal-HitReport"
        body={
          <div
            id="hitsreportoptionsdialogue-modal-container"
            className="h-[100%] w-[100%] flex flex-col gap-[0.5rem]"
          >
            <div
              id="hitsreportoptionsdialogue-modal-information-text"
              className=" text-[#5D5D5D] text-[1rem] w-[100%]"
            >
              Please provide information you want to include into the report
            </div>
            <div
              id="hitsreportoptionsdialogue-modal-content-section"
              className="flex flex-row h-[93.2%] justify-between w-[100%]"
            >
              <div
                id="hitsreportoptionsdialogue-modal-drop-score-container"
                className="flex flex-col w-[59.46%] h-[99.44%] gap-[1rem] justify-space-between"
              >
                <div
                  id="hitsreportoptionsdialogue-modal-drop-section"
                  className="flex flex-row flex-gap-1 h-[5.8%] items-center"
                >
                  <text className="text-[#333333] text-[0.875rem]">Drop</text>
                  <select
                    name="liquidClass"
                    id="hitsreportoptionsdialogue-modal-drop-liquidClass"
                    onChange={(e) => {
                      setSelectedDrop(parseInt(e.target.value));
                    }}
                    value={selectedDrop}
                    style={{ width: '4rem' }}
                    className="inputFieldDetails h-[2rem] w-[8.5rem]"
                    placeholder="Select Drop"
                  >
                    {createOptions()}
                  </select>
                </div>

                <div
                  id="hitsreportoptionsdialogue-modal-score-select-all-checkbox"
                  className="flex flex-row items-center h-[4.6%] gap-[0.25rem]"
                >
                  <FmlxCheckBox
                    id="hitsreportoptionsdialogue-modal-score-select-all-checkbox"
                    checked={selectAllCheckboxState.checked}
                    indeterminate={selectAllCheckboxState.indeterminate}
                    onChange={(e) => allGroupSelectionHandler(e.target.checked)}
                    size={FmlxCheckBox.Size.MEDIUM}
                  />
                  <div
                    id="hitsreportoptionsdialogue-modal-score-select-all-text"
                    className="text-label text-[#4D4D4D] text-[1rem]"
                  >
                    Select All Scores
                  </div>
                </div>

                <div
                  id="hitsreportoptionsdialogue-modal-score-groups"
                  className=" h-[77.6%]"
                  style={{ overflow: 'auto', height: '25rem' }}
                >
                  {scoreGroups.map((sg, index) => {
                    const groupCheckboxState = getCheckboxState(sg.scores);
                    return (
                      <div
                        id={`hitsreportoptionsdialogue-modal-score-group-${index}`}
                        key={`hits-reports-score-group-${index}`}
                        className="d-flex flex-column mb-2 justify-content-center"
                      >
                        <div className="d-flex flex-row align-items-center gap-[0.25rem]">
                          {sg.scores.length > 0 ? (
                            <div
                              id={`hitsreportoptionsdialogue-modal-score-group-${index}-toggle`}
                              role="button"
                              tabIndex={0}
                              className="hits-report-score-chevron"
                              style={{
                                display: 'inline-block',
                                cursor: 'pointer',
                                minWidth: 16,
                                minHeight: 16,
                                marginRight: 4,
                              }}
                              onClick={() => toggleGroupCollapse(sg.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleGroupCollapse(sg.id);
                                }
                              }}
                              aria-label={`${sg.expanded ? 'Collapse' : 'Expand'} ${
                                sg.name
                              }`}
                              aria-expanded={sg.expanded}
                            >
                              <RMSvgIcon
                                id={`hitsreportoptionsdialogue-modal-score-group-${index}-toggle-icon`}
                                height={16}
                                width={16}
                                name="ArrowDown"
                                style={sg.expanded ? {} : { transform: 'rotate(-90deg)' }}
                              />
                            </div>
                          ) : (
                            <div
                              style={{ minWidth: 16, minHeight: 16, marginRight: 4 }}
                            />
                          )}
                          <FmlxCheckBox
                            id={`hitsreportoptionsdialogue-modal-score-group-${index}-checkbox`}
                            checked={groupCheckboxState.checked}
                            indeterminate={groupCheckboxState.indeterminate}
                            onChange={(e) =>
                              scoreGroupsChangeHandler(e.target.checked, sg.id)
                            }
                            size={FmlxCheckBox.Size.MEDIUM}
                          />
                          <div
                            id={`hitsreportoptionsdialogue-modal-score-group-${sg.name}-text`}
                            className="text-label text-[#4D4D4D] text-[1rem] "
                          >
                            {sg.name}
                          </div>
                        </div>
                        {sg.expanded && (
                          <div className="hits-report-score-children ml-4 mt-1">
                            {sg.scores.map((score) => (
                              <div
                                key={`hits-reports-score-${sg.id}-${score.id}`}
                                className="d-flex flex-row align-items-center mb-1"
                              >
                                <div
                                  style={{ minWidth: 16, minHeight: 16, marginRight: 4 }}
                                />
                                <FmlxCheckBox
                                  id={`hitsreportoptionsdialogue-modal-score-${sg.id}-${score.id}-checkbox`}
                                  checked={score.selected}
                                  onChange={(e) =>
                                    scoreSelectionHandler(
                                      e.target.checked,
                                      sg.id,
                                      score.id,
                                    )
                                  }
                                  size={FmlxCheckBox.Size.MEDIUM}
                                />
                                <div className="text-label text-[#4D4D4D] text-[1rem]">
                                  {score.name}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div
                  id="hitsreportoptionsdialogue-modal-latest-inspection-section"
                  className="flex flex-row h-[4.6%] items-center"
                >
                  <FmlxCheckBox
                    id="hitsreportoptionsdialogue-modal-latest-inspection-checkbox"
                    checked={latestInspectionOnly}
                    onChange={(e) => {
                      setLatestInspectionOnly(e.target.checked);
                    }}
                    size={FmlxCheckBox.Size.MEDIUM}
                  />
                  <div
                    id="hitsreportoptionsdialogue-modal-latest-inspection-text"
                    className="text-label text-[#4D4D4D] text-[1rem]"
                  >
                    Use score from latest inspection only
                  </div>
                </div>
                <div
                  id="hitsreportoptionsdialogue-modal-uv-hits-only-section"
                  className="d-flex flex-row align-items-baseline h-[7%]"
                >
                  <RMCheckBoxY
                    isChecked={uvHitsOnly}
                    disabled={!isSherlock4Selected}
                    onChange={(e) => {
                      setUvHitsOnly(e);
                    }}
                  />
                  <div
                    id="hitsreportoptionsdialogue-modal-uv-hits-only-text"
                    className="text-label"
                    style={{ opacity: isSherlock4Selected ? 1 : 0.6 }}
                  >
                    UV Hits only
                  </div>
                </div>
              </div>

              <div className="w-[1px] bg-[#CACACA] h-[100%]"></div>

              <div
                id="hitsreportoptionsdialogue-modal-capture-profile-section"
                className="flex flex-col w-[36.57%] h-[100%]"
              >
                <div
                  id="hitsreportoptionsdialogue-modal-capture-profile-table"
                  className="h-[95%] min-h-0"
                >
                  <HitsReportCaptureProfileTable
                    id="hitsreportoptionsdialogue-modal-capture-profile"
                    columns={captureProfileColumns}
                    captureProfiles={captureProfiles}
                    onProfileChange={captureProfilesChangeHandler}
                    customHeight="100%"
                  />
                </div>
                <div
                  id="hitsreportoptionsdialogue-modal-capture-profile-status"
                  className="h-[5%] content-center"
                >
                  <div
                    id="hitsreportoptionsdialogue-modal-capture-profile-maximum-selection-status"
                    className="text-p text-error"
                  >
                    {selectedCaptureProfileCount === 2 ? 'Maximum seletion reached!' : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        onOk={() => hitReportsHandler(false)}
        onSave={() => hitReportsHandler(true)}
        okayDisabled={generateReportDisabled}
        saveDisabled={generateReportDisabled}
        okButtonText="Preview"
        noButtonText="Close"
        saveButtonText="Save"
        isSaveAvailable={true}
        heading="Hits Report Options"
        size={FmlxModal.Size.EXTRA_LARGE}
      />
      <LoadingModal showModal={isLoading} text={'Loading'} />
      <RMModal
        id="hitsreportoptionsdialogue-configurehits-modal"
        showModal={showNoHitError}
        setShowModal={setShowNoHitError}
        body="You have no hits here. Have you configured hits?"
        heading="Error"
        onOk={() => {
          setShowNoHitError(false);
        }}
        okButtonText="OK"
        isOkOnly
      />
    </>
  );
};
